<template>
    <v-container :fluid="true" class="max-height">
        <!-- Header -->
        <v-row class="mb-4">
            <v-col cols="12" class="d-flex justify-space-between align-center px-5">
                <div>
                    <h1 class="text-h4 font-weight-bold">Drivers</h1>
                    <p class="text-body-2 text-grey-darken-1 mt-1">Manage your fleet drivers and their assignments</p>
                </div>
                <v-btn color="primary" prepend-icon="mdi-plus" size="large" rounded="lg" elevation="2"
                       @click="createDialogSwitch = true">
                    Add Driver
                </v-btn>
            </v-col>
        </v-row>

        <!-- Search & Filters Bar -->
        <v-row class="px-5 mb-2">
            <v-col cols="12" md="4">
                <v-text-field v-model="searchQuery" density="compact" variant="outlined" placeholder="Search drivers..."
                              prepend-inner-icon="mdi-magnify" clearable hide-details rounded="lg"/>
            </v-col>
            <v-col cols="12" md="3">
                <v-select v-model="statusFilter" :items="statusOptions" density="compact" variant="outlined"
                          label="Status" clearable hide-details rounded="lg"/>
            </v-col>
            <v-col cols="12" md="3">
                <v-select v-model="drivingFilter" :items="drivingOptions" density="compact" variant="outlined"
                          label="Driving Status" clearable hide-details rounded="lg"/>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center justify-end">
                <v-btn-toggle v-model="viewMode" density="compact" rounded="lg" mandatory color="primary">
                    <v-btn value="grid" icon="mdi-view-grid" size="small"/>
                    <v-btn value="table" icon="mdi-view-list" size="small"/>
                </v-btn-toggle>
            </v-col>
        </v-row>

        <v-row v-if="credentialsExpiring" class="px-5 mb-2">
            <v-col cols="12">
                <v-chip closable color="warning" variant="tonal" prepend-icon="mdi-card-account-details-outline"
                        @click:close="clearCredentialsFilter">
                    Credentials expired or expiring within 30 days
                </v-chip>
            </v-col>
        </v-row>

        <!-- Stats Summary -->
        <v-row class="px-5 mb-4">
            <v-col cols="12" sm="4">
                <v-card variant="tonal" color="primary" rounded="lg" class="pa-3">
                    <div class="text-caption text-grey-darken-1">Total Drivers</div>
                    <div class="text-h5 font-weight-bold">{{ totalDrivers }}</div>
                </v-card>
            </v-col>
            <v-col cols="12" sm="4">
                <v-card variant="tonal" color="success" rounded="lg" class="pa-3">
                    <!-- Page-local count until API supports aggregate stats -->
                    <div class="text-caption text-grey-darken-1">Currently Driving</div>
                    <div class="text-h5 font-weight-bold">{{ drivers.filter(d => d.current_status).length }}</div>
                </v-card>
            </v-col>
            <v-col cols="12" sm="4">
                <v-card variant="tonal" color="warning" rounded="lg" class="pa-3">
                    <div class="text-caption text-grey-darken-1">License Expiring</div>
                    <div class="text-h5 font-weight-bold">{{ expiringLicenseCount }}</div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Loading -->
        <v-row v-if="loading" justify="center" class="py-12">
            <v-progress-circular indeterminate color="primary" size="48"/>
        </v-row>

        <!-- Grid View -->
        <v-row v-else-if="viewMode === 'grid' && filteredDrivers.length" class="px-3">
            <v-col v-for="driver in filteredDrivers" :key="driver.id" cols="12" sm="6" md="4" lg="3">
                <v-card class="driver-card" rounded="xl" elevation="2" hover style="cursor: pointer;"
                        @click="goToDriver(driver)">
                    <!-- Card Header with status indicator -->
                    <div class="driver-card-header pa-4 pb-2">
                        <div class="d-flex justify-space-between align-start">
                            <div class="d-flex align-center ga-3">
                                <div class="position-relative">
                                    <UserAvatar :src="driver.user_avatar || driver.profile_image"
                                                :first-name="driver.user_first_name || driver.first_name"
                                                :last-name="driver.user_last_name || driver.last_name" size="56"/>
                                    <!-- Online/driving indicator dot -->
                                    <div v-if="driver.current_status" class="status-dot status-driving"/>
                                    <div v-else-if="driver.status" class="status-dot status-idle"/>
                                </div>
                                <div>
                                    <div class="d-flex align-center ga-1">
                                        <h3 class="text-subtitle-1 font-weight-bold text-truncate" style="max-width: 140px">
                                            {{ driver.user_first_name || driver.first_name }} {{ driver.user_last_name || driver.last_name }}
                                        </h3>
                                        <v-chip v-if="driver.driver_score != null" size="x-small" variant="flat"
                                                :color="driverScoreColor(driver.driver_score)">
                                            {{ driver.driver_score }}
                                        </v-chip>
                                    </div>
                                    <div class="text-caption text-grey-darken-1">{{ driver.department || 'No department' }}</div>
                                </div>
                            </div>
                            <v-menu>
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" icon="mdi-dots-vertical" size="x-small" variant="text"
                                           @click.stop/>
                                </template>
                                <v-list density="compact" min-width="160">
                                    <v-list-item prepend-icon="mdi-pencil" title="Edit"
                                                 @click.stop="editDriver(driver)"/>
                                    <v-divider/>
                                    <v-list-item prepend-icon="mdi-delete" title="Delete" class="text-error"
                                                 @click.stop="deleteDriver(driver)"/>
                                </v-list>
                            </v-menu>
                        </div>
                    </div>

                    <!-- Card Body -->
                    <v-card-text class="pt-0">
                        <!-- Contact Info -->
                        <div class="d-flex flex-column ga-1 mb-3">
                            <div v-if="driver.user_email || driver.email" class="d-flex align-center ga-2 text-caption">
                                <v-icon size="14" color="grey">mdi-email-outline</v-icon>
                                <span class="text-truncate">{{ driver.user_email || driver.email }}</span>
                            </div>
                            <div v-if="driver.user_phone || driver.phone" class="d-flex align-center ga-2 text-caption">
                                <v-icon size="14" color="grey">mdi-phone-outline</v-icon>
                                <span>{{ driver.user_phone || driver.phone }}</span>
                                <v-spacer/>
                                <v-btn :href="`tel:${cleanPhone(driver.user_phone || driver.phone)}`" icon="mdi-phone"
                                       size="x-small" variant="text" color="primary" density="comfortable" @click.stop/>
                                <v-btn :href="`sms:${cleanPhone(driver.user_phone || driver.phone)}`" icon="mdi-message-text"
                                       size="x-small" variant="text" color="primary" density="comfortable" @click.stop/>
                            </div>
                        </div>

                        <!-- Chips Row -->
                        <div class="d-flex flex-wrap ga-1">
                            <v-chip v-if="driver.current_status" size="x-small" color="success" variant="tonal"
                                    prepend-icon="mdi-steering">Driving</v-chip>
                            <v-chip v-else size="x-small" color="grey" variant="tonal"
                                    prepend-icon="mdi-car-off">Idle</v-chip>

                            <v-chip :color="driver.status ? 'primary' : 'grey'" size="x-small" variant="tonal">
                                {{ driver.status ? 'Active' : 'Inactive' }}
                            </v-chip>

                            <v-chip v-if="driver.slno" size="x-small" color="deep-purple" variant="tonal"
                                    prepend-icon="mdi-access-point">{{ driver.slno }}</v-chip>

                            <v-chip v-if="driver.rfid" size="x-small" variant="tonal"
                                    prepend-icon="mdi-nfc-variant">{{ driver.rfid }}</v-chip>

                            <v-chip v-if="isLicenseExpiringSoon(driver)" size="x-small" color="warning" variant="tonal"
                                    prepend-icon="mdi-alert">License expiring</v-chip>
                        </div>

                        <!-- License Info -->
                        <div v-if="driver.license_number" class="mt-3 pa-2 rounded-lg" style="background: #f8f9fa">
                            <div class="d-flex align-center ga-2 text-caption">
                                <v-icon size="14" color="grey-darken-1">mdi-card-account-details</v-icon>
                                <span class="font-weight-medium">{{ driver.license_number }}</span>
                                <v-chip v-if="driver.license_class" size="x-small" variant="outlined" class="ml-auto">
                                    Class {{ driver.license_class }}
                                </v-chip>
                            </div>
                            <div v-if="driver.license_expiry" class="text-caption text-grey mt-1 ml-5">
                                Expires: {{ formatDateOnly(driver.license_expiry) }}
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Table View -->
        <v-row v-else-if="viewMode === 'table' && filteredDrivers.length" class="px-3">
            <v-col cols="12">
                <v-card rounded="xl" elevation="2">
                    <v-table hover>
                        <thead>
                        <tr>
                            <th></th>
                            <th class="cursor-pointer" @click="toggleSort('name')">
                                Name <v-icon v-if="sortBy === 'name'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th v-if="user.type === 'admin'" class="cursor-pointer" @click="toggleSort('company_name')">
                                Company <v-icon v-if="sortBy === 'company_name'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('department')">
                                Department <v-icon v-if="sortBy === 'department'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('email')">
                                Contact <v-icon v-if="sortBy === 'email'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('slno')">
                                Device <v-icon v-if="sortBy === 'slno'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('license_number')">
                                License <v-icon v-if="sortBy === 'license_number'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('driver_score')">
                                Score <v-icon v-if="sortBy === 'driver_score'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th class="cursor-pointer" @click="toggleSort('status')">
                                Status <v-icon v-if="sortBy === 'status'" :icon="sortIcon" size="x-small"/>
                            </th>
                            <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="driver in filteredDrivers" :key="driver.id" style="cursor: pointer;"
                            @click="goToDriver(driver)">
                            <td>
                                <div class="position-relative d-inline-block">
                                    <UserAvatar :src="driver.user_avatar || driver.profile_image"
                                                :first-name="driver.user_first_name || driver.first_name"
                                                :last-name="driver.user_last_name || driver.last_name" size="32"/>
                                    <div v-if="driver.current_status" class="status-dot-sm status-driving"/>
                                </div>
                            </td>
                            <td>
                                <div class="font-weight-medium">{{ driver.user_first_name || driver.first_name }} {{ driver.user_last_name || driver.last_name }}</div>
                            </td>
                            <td v-if="user.type === 'admin'">{{ driver.company_name }}</td>
                            <td class="text-caption">{{ driver.department }}</td>
                            <td>
                                <div class="text-caption">{{ driver.user_email || driver.email }}</div>
                                <div class="text-caption text-grey">{{ driver.user_phone || driver.phone }}</div>
                            </td>
                            <td>
                                <v-chip v-if="driver.slno" size="x-small" variant="tonal" color="deep-purple"
                                        prepend-icon="mdi-access-point">{{ driver.slno }}</v-chip>
                            </td>
                            <td>
                                <div v-if="driver.license_number" class="text-caption">
                                    {{ driver.license_number }}
                                    <v-chip v-if="isLicenseExpiringSoon(driver)" size="x-small" color="warning"
                                            variant="tonal" class="ml-1">!</v-chip>
                                </div>
                            </td>
                            <td>
                                <v-chip v-if="driver.driver_score != null" size="x-small" variant="flat"
                                        :color="driverScoreColor(driver.driver_score)">
                                    {{ driver.driver_score }}
                                </v-chip>
                                <span v-else class="text-caption text-grey">-</span>
                            </td>
                            <td>
                                <v-chip :color="driver.status ? 'success' : 'grey'" size="x-small" variant="tonal">
                                    {{ driver.status ? 'Active' : 'Inactive' }}
                                </v-chip>
                            </td>
                            <td class="text-center">
                                <v-menu>
                                    <template #activator="{ props }">
                                        <v-btn v-bind="props" icon="mdi-dots-vertical" size="x-small" variant="text"
                                               @click.stop/>
                                    </template>
                                    <v-list density="compact" min-width="160">
                                        <v-list-item prepend-icon="mdi-pencil" title="Edit"
                                                     @click.stop="editDriver(driver)"/>
                                        <v-divider/>
                                        <v-list-item prepend-icon="mdi-delete" title="Delete" class="text-error"
                                                     @click.stop="deleteDriver(driver)"/>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                        </tbody>
                    </v-table>
                </v-card>
            </v-col>
        </v-row>

        <!-- Empty State -->
        <v-row v-else-if="!loading" justify="center" class="py-12">
            <v-col cols="12" class="text-center">
                <v-icon size="80" color="grey-lighten-2">mdi-account-group</v-icon>
                <h3 class="text-h6 text-grey-darken-1 mt-4">No drivers found</h3>
                <p class="text-body-2 text-grey mt-1">
                    {{ searchQuery ? 'Try adjusting your search or filters' : 'Add your first driver to get started' }}
                </p>
                <v-btn v-if="!searchQuery" color="primary" prepend-icon="mdi-plus" class="mt-4"
                       @click="createDialogSwitch = true">Add Driver</v-btn>
            </v-col>
        </v-row>

        <!-- Pagination -->
        <v-row v-if="totalPages > 1" justify="center" class="mt-2">
            <v-pagination v-model="currentPage" :length="totalPages" density="compact" rounded="lg"
                          @update:modelValue="fetchDrivers"/>
        </v-row>
    </v-container>

    <!-- Dialogs -->
    <Create-Driver v-if="createDialogSwitch" v-model="createDialogSwitch" @addDriver="addDriver"/>
    <Edit-Driver v-if="editDialogSwitch" v-model="editDialogSwitch" :driver="selectedDriver"
                 @updateDriver="updateDriver"/>
</template>

<script setup>
import {onMounted, ref, computed, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateOnly} from '@/composables/useTimezone';
import EditDriver from "@/components/dialogs/drivers/Edit.vue";
import CreateDriver from "@/components/dialogs/drivers/Create.vue";
import UserAvatar from "@/components/UserAvatar.vue";

const router = useRouter();
const route = useRoute();
const {user} = useAuthStore();
const drivers = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const totalDrivers = ref(0);
const {showSnackbar} = useSnackbarStore();
const createDialogSwitch = ref(false);
const editDialogSwitch = ref(false);
const selectedDriver = ref(null);
const viewMode = ref('grid');
const searchQuery = ref('');
const statusFilter = ref(null);
const drivingFilter = ref(null);
let searchDebounceTimer = null;
// ?credentials=expiring, from the menu count: drivers with a credential expired or expiring within 30 days.
const credentialsExpiring = computed(() => route.query.credentials === 'expiring');
const clearCredentialsFilter = () => {
    const query = {...route.query};
    delete query.credentials;
    router.replace({query});
};

const sortBy = ref(null);
const sortDir = ref('asc');
const sortIcon = computed(() => sortDir.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down');

const toggleSort = (column) => {
    if (sortBy.value === column) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortDir.value = 'asc';
    }
};

const statusOptions = [
    {title: 'Active', value: 1},
    {title: 'Inactive', value: 0},
];
const drivingOptions = [
    {title: 'Driving', value: 1},
    {title: 'Idle', value: 0},
];

const filteredDrivers = computed(() => {
    let result = drivers.value;
    if (statusFilter.value !== null && statusFilter.value !== undefined) {
        result = result.filter(d => d.status === statusFilter.value);
    }
    if (drivingFilter.value !== null && drivingFilter.value !== undefined) {
        result = result.filter(d => (d.current_status ? 1 : 0) === drivingFilter.value);
    }
    if (sortBy.value) {
        const col = sortBy.value;
        const dir = sortDir.value === 'asc' ? 1 : -1;
        result = [...result].sort((a, b) => {
            let va, vb;
            if (col === 'name') {
                va = ((a.user_first_name || a.first_name || '') + ' ' + (a.user_last_name || a.last_name || '')).toLowerCase();
                vb = ((b.user_first_name || b.first_name || '') + ' ' + (b.user_last_name || b.last_name || '')).toLowerCase();
            } else if (col === 'email') {
                va = (a.user_email || a.email || '').toLowerCase();
                vb = (b.user_email || b.email || '').toLowerCase();
            } else if (col === 'driver_score') {
                va = parseFloat(a.driver_score) || 0;
                vb = parseFloat(b.driver_score) || 0;
            } else if (col === 'status') {
                va = a.status ? 1 : 0;
                vb = b.status ? 1 : 0;
            } else {
                va = (a[col] || '').toString().toLowerCase();
                vb = (b[col] || '').toString().toLowerCase();
            }
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
    }
    return result;
});

// Note: expiringLicenseCount stat is page-local until the API supports aggregate stats
const expiringLicenseCount = computed(() => {
    return drivers.value.filter(d => isLicenseExpiringSoon(d)).length;
});

const driverScoreColor = (score) => {
    if (score >= 8.0) return 'success'
    if (score >= 5.0) return 'warning'
    return 'error'
}

const isLicenseExpiringSoon = (driver) => {
    if (!driver.license_expiry) return false;
    const expiry = new Date(driver.license_expiry);
    const now = new Date();
    const daysUntilExpiry = (expiry - now) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
};

onMounted(() => fetchDrivers());

// Opened from the menu count (or the chip cleared): show exactly that list.
watch(credentialsExpiring, () => {
    if (route.name !== 'drivers') return;
    statusFilter.value = null;
    drivingFilter.value = null;
    currentPage.value = 1;
    if (searchQuery.value) searchQuery.value = '';
    else fetchDrivers();
});

// Debounce search to trigger server-side reload
watch(searchQuery, () => {
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
        currentPage.value = 1;
        fetchDrivers();
    }, 400);
});

const fetchDrivers = () => {
    loading.value = true;
    const params = new URLSearchParams({page: currentPage.value});
    if (searchQuery.value) {
        params.append('search', searchQuery.value);
    }
    if (credentialsExpiring.value) {
        params.append('credentials', 'expiring');
    }
    send('get', `companies/drivers?${params.toString()}`)
        .then((response) => {
            drivers.value = response.data.data.data;
            totalPages.value = response.data.data.last_page ?? response.data.last_page ?? 1;
            totalDrivers.value = response.data.data.total ?? totalDrivers.value;
            currentPage.value = response.data.data.current_page ?? currentPage.value;
        })
        .catch(() => showSnackbar('Drivers could not be loaded', 'error'))
        .finally(() => loading.value = false);
};

const addDriver = (driver) => drivers.value.unshift(driver);

const editDriver = (driver) => {
    selectedDriver.value = driver;
    editDialogSwitch.value = true;
};

const goToDriver = (driver) => router.push(`/drivers/${driver.id}`);

// Keep only digits and + so tel:/sms: links are valid dial strings.
const cleanPhone = (p) => (p || "").replace(/[^\d+]/g, "");

const updateDriver = (driver) => {
    const idx = drivers.value.findIndex(d => d.id === driver.id);
    if (idx !== -1) drivers.value[idx] = driver;
};

const deleteDriver = (driver) => {
    const driverName = (driver.user_first_name || driver.first_name || '') + ' ' + (driver.user_last_name || driver.last_name || '');
    // First call without confirm to get impact summary
    send('delete', `companies/drivers/${driver.id}`)
        .then((response) => {
            if (response.data.success) {
                const impact = response.data.data?.impact;
                let message = `Are you sure you want to delete "${driverName.trim()}"?`;
                if (impact) {
                    const parts = [];
                    if (impact.trips > 0) parts.push(`${impact.trips} trip(s) will lose their driver reference`);
                    if (impact.assets > 0) parts.push(`${impact.assets} asset(s) will be unassigned from this driver`);
                    if (parts.length) message += '\n\n' + parts.join('\n');
                }
                if (!confirm(message)) return;

                // Confirmed - delete with confirm=true
                send('delete', `companies/drivers/${driver.id}?confirm=true`)
                    .then((res) => {
                        if (res.data.success) {
                            drivers.value = drivers.value.filter(d => d.id !== driver.id);
                            showSnackbar(res.data.message);
                        }
                    })
                    .catch(() => showSnackbar('Driver could not be deleted', 'error'));
            }
        })
        .catch(() => showSnackbar('Driver could not be deleted', 'error'));
};
</script>

<style scoped>
.driver-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.driver-card:hover {
    transform: translateY(-2px);
}
.status-dot {
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid white;
}
.status-dot-sm {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid white;
}
.status-driving {
    background: #4caf50;
    animation: pulse 2s infinite;
}
.status-idle {
    background: #9e9e9e;
}
@keyframes pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4); }
    50% { box-shadow: 0 0 0 4px rgba(76, 175, 80, 0); }
}
</style>
